import styled, { css } from 'styled-components'

import Link from '../../global/Link'

interface DispensaryCardProps {
  backgroundGraphic?: string
  isSelected?: boolean
}

interface HigherOrderStyles {
  (styles: ReturnType<typeof css>): ReturnType<typeof css>
}

/**
 * Standard media query for rendering components in viewport >= 520px wide
 */
const wideQueryFluidLayout: HigherOrderStyles = (styles) => css`
  @media (min-width: 520px) {
    ${styles};
  }
`

/**
 * Media query for rendering components in container >= 520px wide, tailored to
 * constraints of the Account section layout. This layout provides a narrow container
 * between 768px and 900px viewport widths.
 */
const wideQueryContainedLayout: HigherOrderStyles = (styles) => css`
  @media (min-width: 520px) and (max-width: 767px), (min-width: 900px) {
    ${styles};
  }
`

export const commonDispensaryCardStyles = (wideQueryFactory: HigherOrderStyles) => css`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 45px 30px 30px;
  max-width: 600px;
  width: 100%;
  height: auto;
  border-radius: 25px;
  background-color: ${({ theme }) => theme.colors.coolGray.cool150};
  box-sizing: border-box;
  text-align: left;
  transition: 0.3s;

  ${wideQueryFactory(css`
    padding-left: 45px;
    // pad right for background image
    padding-right: 140px;
    max-height: 550px;
  `)};
`

const elevated = css`
  box-shadow: ${({ theme }) => `0 3px 5px -1px ${theme.colors.coolGray.cool900}33,
  0 6px 10px 0 ${theme.colors.coolGray.cool900}23,
  0 1px 18px 0 ${theme.colors.coolGray.cool900}1E`};
`

export const DispensaryCardFluidSelectable = styled('button')<DispensaryCardProps>`
  ${commonDispensaryCardStyles(wideQueryFluidLayout)};
  ${({ backgroundGraphic }) =>
    wideQueryFluidLayout(css`
      background-image: url(${backgroundGraphic});
      background-position: center right -210px;
      background-repeat: no-repeat;
      background-size: 1100px;
    `)};
  box-sizing: border-box;
  cursor: pointer;
  margin: 30px 0 15px;
  border-width: 2px;
  border-style: solid;

  ${({ isSelected, theme }) =>
    isSelected
      ? css`
          border-color: ${theme.colors.brand.blue};
          transform: translateY(-1px);
          ${elevated};
        `
      : css`
          border-color: transparent;
          &:hover {
            transform: translateY(-1px);
            ${elevated};
          }
        `};
`

export const DispensaryCardFluid = styled.div<Omit<DispensaryCardProps, 'isSelected'>>`
  ${commonDispensaryCardStyles(wideQueryFluidLayout)};
  ${({ backgroundGraphic }) =>
    wideQueryFluidLayout(css`
      background-image: url(${backgroundGraphic});
      background-position: center right -150px;
      background-repeat: no-repeat;
    `)};
  margin-bottom: 45px;
`

export const DispensaryCardContained = styled.div<Omit<DispensaryCardProps, 'isSelected'>>`
  ${commonDispensaryCardStyles(wideQueryContainedLayout)};
  ${({ backgroundGraphic }) =>
    wideQueryContainedLayout(css`
      background-image: url(${backgroundGraphic});
      background-position: center right -150px;
      background-repeat: no-repeat;
    `)};
`

/**
 * Render wide component styles for each media query (default and account layout)
 * filtered by parent
 */
const wideQueries: HigherOrderStyles = (styles) => css`
  ${DispensaryCardFluid} &, ${DispensaryCardFluidSelectable} & {
    ${wideQueryFluidLayout(styles)};
  }
  ${DispensaryCardContained} & {
    ${wideQueryContainedLayout(styles)};
  }
`

export const SelectedMark = styled('div')`
  display: inline-block;
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%) translateY(-50%);
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.component.button};
  font-size: 15px;
  letter-spacing: 0.1em;
  line-height: 1.5;
  text-align: center;
  background: ${({ theme }) => theme.colors.brand.blue};
  color: ${({ theme }) => theme.colors.brand.white};
  width: fit-content;
  padding: 0.4em 1em;
  border-radius: 5px;
  pointer-events: none;
`

export const TitleContainer = styled('div')`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  height: auto;
  padding: 0;
  overflow: hidden;
  ${wideQueries(css`
    flex-direction: row;
    text-align: left;
  `)};
`

export const DispensaryPicture = styled('img')`
  width: 60px;
`

export const DispensaryName = styled('p')`
  padding: 5px 0;
  margin: 0;
  font-size: 20px;
  font-weight: 600;

  ${wideQueries(css`
    margin-left: 30px;
  `)};
`

export const DispensarySpecialty = styled('p')`
  margin: 0;
  font-size: 16px;

  ${wideQueries(css`
    margin-left: 30px;
  `)};
`

export const DisplayColumn = styled('div')`
  display: flex;
  flex-direction: column;
`

export const InfoContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
`

export const InfoLink = styled(Link)`
  display: inherit;
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`

export const InfoList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 15px 0 0;
`

export const InfoItem = styled('li')`
  display: flex;
  align-items: center;
  margin: 15px 0 0;
  padding: 0;
  font-size: 14px;
  overflow-wrap: anywhere;

  ${wideQueries(css`
    font-size: 16px;
  `)};
`

export const InfoIcon = styled('img')`
  width: 25px;
  margin-right: 15px;
`

export const InfoText = styled.span`
  // Keep text from getting cut off
  max-width: 330px;
`

export const PractitionerBlurb = styled('p')`
  font-size: 12px;
  margin: 15px 0 0;
  overflow: hidden;
  // limit to 3 lines
  max-height: ${12 * 1.43 * 3}px;
  overflow-y: auto;
`
